import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';

const RECENTLY_VIEWED_KEY = 'palacesamples_recently_viewed';
const MAX_RECENT = 6;

function loadRecentlyViewed(): string[] {
  try {
    const raw = localStorage.getItem(RECENTLY_VIEWED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_RECENT) : [];
  } catch {
    return [];
  }
}

function saveRecentlyViewed(ids: string[]) {
  try {
    localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(ids.slice(0, MAX_RECENT)));
  } catch {
    // ignore
  }
}

interface RecentlyViewedContextType {
  recentlyViewedIds: string[];
  addViewed: (id: string) => void;
}

const RecentlyViewedContext = createContext<RecentlyViewedContextType | undefined>(undefined);

export function RecentlyViewedProvider({ children }: { children: React.ReactNode }) {
  const [recentlyViewedIds, setRecentlyViewedIds] = useState<string[]>(loadRecentlyViewed);

  useEffect(() => {
    saveRecentlyViewed(recentlyViewedIds);
  }, [recentlyViewedIds]);

  const addViewed = useCallback((id: string) => {
    setRecentlyViewedIds((prev) => {
      const filtered = prev.filter((x) => x !== id);
      return [id, ...filtered].slice(0, MAX_RECENT);
    });
  }, []);

  return (
    <RecentlyViewedContext.Provider value={{ recentlyViewedIds, addViewed }}>
      {children}
    </RecentlyViewedContext.Provider>
  );
}

export function useRecentlyViewed() {
  const ctx = useContext(RecentlyViewedContext);
  if (!ctx) throw new Error('useRecentlyViewed must be used within RecentlyViewedProvider');
  return ctx;
}
